/* eslint-disable brace-style */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, first, tap } from 'rxjs/operators';
import { IamAPI } from './api/core';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
    public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();

    private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
    public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

    /**
     * Publishes `true` if and only if (a) all the asynchronous initial
     * login calls have completed or errorred, and (b) the user ended up
     * being authenticated.
     *
     * In essence, it combines:
     *
     * - the latest known state of whether the user is authorized
     * - whether the ajax calls for initial log in have all been done
     */
    public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([this.isAuthenticated$, this.isDoneLoading$]).pipe(
        map((values) => values.every((b) => b))
    );

    constructor(private oauthService: OAuthService, private router: Router, private iamAPI: IamAPI) {
        // skip login in e2e environment
        if (!environment.e2e) {
            // Useful for debugging:
            // this.oauthService.events.subscribe((event) => {
            //     if (event instanceof OAuthErrorEvent) {
            //         console.error('OAuthErrorEvent Object:', event);
            //     } else {
            //         console.warn('OAuthEvent Object:', event);
            //         if (['token_received'].includes(event.type)) {
            //             this.oauthService.loadUserProfile();
            //         }
            //     }
            // });

            // This is tricky, as it might cause race conditions (where access_token is set in another
            // tab before everything is said and done there.
            // TODO: Improve this setup. See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
            window.addEventListener('storage', (event) => {
                // The `key` is `null` if the event was caused by `.clear()`
                if (event.key !== 'access_token' && event.key !== null) {
                    return;
                }

                console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
                this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

                if (!this.oauthService.hasValidAccessToken()) {
                    this.login();
                }
            });

            this.oauthService.events.subscribe((_) => {
                this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
            });
            this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

            this.oauthService.events
                .pipe(
                    filter((e) => {
                        // console.log(e, ['token_received'].includes(e.type));
                        return ['token_received'].includes(e.type);
                    })
                )
                .subscribe((e) => this.oauthService.loadUserProfile());

            this.oauthService.events
                .pipe(filter((e) => ['session_terminated', 'session_error'].includes(e.type)))
                .subscribe((e) => this.login());

            this.oauthService.setupAutomaticSilentRefresh();
        } else {
            this.isAuthenticatedSubject$.next(true);
            this.isDoneLoadingSubject$.next(true);
            let stateUrl = this.oauthService.state;
            if (stateUrl?.startsWith('/') === false) {
                stateUrl = decodeURIComponent(stateUrl);
            }

            const lastLocation = localStorage.getItem('lastLocation');
            // TODO: find better way to prevent redirect than last location
            this.router.navigateByUrl(stateUrl === '' ? lastLocation || '' : stateUrl || '');
        }
    }

    public runInitialLoginSequence(): Promise<void> {
        // if (location.hash) {
        //     console.log('Encountered hash fragment, plotting as table...');
        //     console.table(
        //         location.hash
        //             .substr(1)
        //             .split('&')
        //             .map((kvp) => kvp.split('='))
        //     );
        // }

        // skip login in e2e environment
        if (!environment.e2e) {
            // 0. LOAD CONFIG:
            // First we have to check to see how the IdServer is
            // currently configured:
            return (
                this.oauthService
                    .loadDiscoveryDocument()

                    // // 1. HASH LOGIN:
                    // // Try to log in via hash fragment after redirect back
                    // // from IdServer from tryLoginCodeFlow:
                    .then(() => this.oauthService.tryLoginCodeFlow())

                    .then(() => {
                        // console.log('checkAccessToken...');
                        if (this.oauthService.hasValidAccessToken()) {
                            // console.log('AccessToken available!');
                            return Promise.resolve();
                        }
                        // console.log('No AccessToken!');
                        // this.oauthService.getRefreshToken();
                        // 2. SILENT LOGIN:
                        // Try to log in via a refresh because then we can prevent
                        // needing to redirect the user:
                        // console.log('Trying silent login with refresh token...');
                        return this.oauthService
                            .refreshToken()
                            .then(() => {
                                // console.log('Success!');
                                return Promise.resolve();
                            })
                            .catch((result) => {
                                // console.log('Failure!');
                                // Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
                                // Only the ones where it's reasonably sure that sending the
                                // user to the IdServer will help.
                                const errorResponsesRequiringUserInteraction = [
                                    'interaction_required',
                                    'login_required',
                                    'account_selection_required',
                                    'consent_required',
                                    'invalid_grant',
                                ];
                                // console.log('Checking result...', result);

                                if (result && result.error && errorResponsesRequiringUserInteraction.indexOf(result.error.error) >= 0) {
                                    // 3. ASK FOR LOGIN:
                                    // At this point we know for sure that we have to ask the
                                    // user to log in, so we redirect them to the IdServer to
                                    // enter credentials.
                                    // console.log('Trying login...');
                                    this.login();
                                }

                                // We can't handle the truth, just pass on the problem to the
                                // next handler.
                                return Promise.reject(result);
                            });
                    })

                    .then(() => {
                        this.isDoneLoadingSubject$.next(true);

                        // Check for the strings 'undefined' and 'null' just to be sure. Our current
                        // login(...) should never have this, but in case someone ever calls
                        // initImplicitFlow(undefined | null) this could happen.
                        if (this.oauthService.state !== 'undefined' && this.oauthService.state !== 'null') {
                            const lastLocation = localStorage.getItem('lastLocation');

                            let stateUrl = this.oauthService.state;
                            if (stateUrl?.startsWith('/') === false) {
                                stateUrl = decodeURIComponent(stateUrl);
                            }

                            // enabling automatic refresh via refresh_token https://manfredsteyer.github.io/angular-oauth2-oidc/docs/additional-documentation/refreshing-a-token.html
                            this.oauthService.setupAutomaticSilentRefresh();

                            // TODO: find better way to prevent redirect than last location
                            this.router.navigateByUrl(stateUrl === '' ? lastLocation || '' : stateUrl || '');
                        }
                    })
                    .catch(() => this.isDoneLoadingSubject$.next(true))
            );
        } else {
            // CYPRESS SETS TOKEN ITSELF
            // WE DONT WANT KEYCLOAK HERE BUT THE SAME APP BEHAVIOR
            this.isAuthenticatedSubject$.next(true);
            this.isDoneLoadingSubject$.next(true);
        }
    }

    public login(targetUrl?: string) {
        this.oauthService.initCodeFlow(targetUrl || this.router.url);
    }

    public logout() {
        this.oauthService.logOut();
    }
    public refresh() {
        this.oauthService.silentRefresh();
    }
    public hasValidToken() {
        return this.oauthService.hasValidAccessToken();
    }
}
